import { inject, Injectable } from '@angular/core';
import { Observable, of }     from 'rxjs';
import { catchError, map }    from 'rxjs/operators';

import { AuthService, ConfirmRegistrationData, RegistrationData, ResendConfirmationData, SUCCESS_TYPE } from '../auth/auth.service';
import { environment }                                                                                  from '../../../environments/environment';
import { RestService }                                                                                  from '../rest.service';
import { UserService }                                                                                  from '../../api/user/user.service';
import { SessionService }                                                                               from '../../api/session/session.service';
import { LoginResult }        from '../auth/data/login-result';
import { SessionChangedData } from '../auth/data/session-changed-data';
import { IframeService }      from '../iframe.service';

const SUCCESS_VALUE = 'SUCCESS';

@Injectable({
  providedIn: 'root'
})
export class PingOneIdentityService implements AuthService {
  private restService = inject(RestService);
  private userService = inject(UserService);
  private sessionService = inject(SessionService);

  login(username: string, password: string): Observable<LoginResult> {
    const loginData = {
      loginParameters: {
        username: username,
        password: password
      },
      loginFlow: 'USER_PASSWORD'
    }
    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    return this.restService.post(environment.api.endpoints.login, loginData, {params: runsInIframe});
  }

  logout(): void {
    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    this.restService.delete(environment.api.endpoints.userSession, {}, {params: runsInIframe});
  }

  isAuthenticated(): Observable<SessionChangedData | null> {
    return this.restService.get<SessionChangedData>(environment.api.endpoints.sessionMetadata).pipe(
      catchError(() => of(null))
    );
  }

  refreshSession(): Observable<SessionChangedData> {
    const refreshSessionData = {
      loginParameters: {},
      loginFlow: 'REFRESH_TOKEN'
    };
    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    return this.restService.post(environment.api.endpoints.sessionRefresh, refreshSessionData, {params: runsInIframe});
  }

  sendMFACode(code: string, username: string, mfaOption: string): Observable<LoginResult> {
    const mfaCodeData = {
      code: code,
      username: username,
      mfaOption: mfaOption
    };
    const runsInIframe = {runsInIframe: IframeService.runsInIframe()};
    return this.restService.post(environment.api.endpoints.sendMFACode, mfaCodeData, {params: runsInIframe});
  }

  resendEmailMfa(): Observable<SUCCESS_TYPE> {
    return this.restService.post(environment.api.endpoints.resendEmailMfa, {});
  }

  associateEmailToken(): Observable<SUCCESS_TYPE> {
    return this.restService.post(environment.api.endpoints.associateEmailToken, {});
  }


  verifyEmailToken(code: string): Observable<string> {
    return this.restService.post(environment.api.endpoints.verifyEmailToken, code);
  }

  getUserMfaOptions(): Observable<string[]> {
    return this.userService.getUserMfaSettings();
  }

  disableMfa(): Observable<string> {
    return this.restService.post(environment.api.endpoints.disableMfa, {});
  }

  associateSoftwareToken(): Observable<Record<'secretCode' | 'qrValue', string>> {
    return this.restService.post(environment.api.endpoints.associateSoftwareToken, {})
      .pipe(map((response: { secretCode: string; qrValue: string }) => {
        return {
          secretCode: response.secretCode,
          qrValue: response.qrValue
        };
      }));
  }

  verifySoftwareToken(token: string): Observable<unknown> {
    return this.restService.post(environment.api.endpoints.verifySoftwareToken, token);
  }

  changePassword(oldPassword: string, newPassword: string): Observable<SUCCESS_TYPE> {
    const changePasswordData = {
      oldPassword: oldPassword,
      newPassword: newPassword
    }
    return this.restService
      .post(environment.api.endpoints.changePassword, changePasswordData)
      .pipe(map(_ => SUCCESS_VALUE));
  }

  register(registrationData: RegistrationData): Observable<SUCCESS_TYPE> {
    return this.userService.registerUser(registrationData);
  }

  confirmRegistration(username: string, confirmationCode: string): Observable<SUCCESS_TYPE> {
    const confirmRegistrationData = new ConfirmRegistrationData(username, confirmationCode);
    return this.userService.confirmUserRegistration(confirmRegistrationData);
  }

  forgotPassword(username: string): Observable<SUCCESS_TYPE> {
    return this.restService
      .post(environment.api.endpoints.forgotPassword, {userName: username})
      .pipe(map(_ => SUCCESS_VALUE));
  }

  resendRecoveryCode(username: string): Observable<SUCCESS_TYPE> {
    return this.restService
      .post(environment.api.endpoints.forgotPasswordResend, {userName: username})
      .pipe(map(_ => SUCCESS_VALUE));
  }

  forgotUsername(email: string): Observable<SUCCESS_TYPE> {
    return this.restService
      .post(environment.api.endpoints.forgotUserName, email)
      .pipe(map(_ => SUCCESS_VALUE));
  }

  recoverPassword(verificationCode: string, newPassword: string, userName: string): Observable<SUCCESS_TYPE> {
    return this.restService
      .post(environment.api.endpoints.forgotPasswordRecover, {
        userName: userName,
        validationCode: verificationCode,
        newPassword: newPassword
      })
      .pipe(map(_ => SUCCESS_VALUE));
  }

  resendConfirmationMail(username: string): Observable<SUCCESS_TYPE> {
    const resendConfirmationData = new ResendConfirmationData(username);
    return this.userService.resendUserConfirmation(resendConfirmationData);
  }

  notifyMfaSetup(): Observable<SUCCESS_TYPE> {
    return new Observable<SUCCESS_TYPE>(observer => {
        this.sessionService.markSessionDirty();

        observer.next(SUCCESS_VALUE);
        observer.complete();
    });
  }

  notifyEmailMfaSetup(): Observable<SUCCESS_TYPE> {
    return new Observable<SUCCESS_TYPE>(observer => {
      this.sessionService.markSessionDirty();

      observer.next(SUCCESS_VALUE);
      observer.complete();
    });
  }

  getUserSessionToken(): Observable<string> {
    return this.restService.get(environment.api.endpoints.userSessionToken, {responseType: 'text'});
  }

}